 
import { Component, Fragment } from 'react';
import { Nav, AccountSelector } from 'toolkit/components/index';
import { get } from '../../../utils/shared/api';
import IaPermissionMapper from './ia_permission_mapper';
import loadingIaMap from './ia_map_loading';
import IaMapManager from './ia_map_manager';
import LinkPermissionManager from './link_permission_manager';
import AccountSwitcherLoader, { Account, Publication } from './account_switcher_loader';
import NullAccountSwitcherLoader from './null_account_switcher_loader';
import CookieJar from '../../utils/cookie_jar';
import { logError } from '../../utils/errors';
import PlatformLinkBuilder from '../../../utils/integration/PlatformLinkBuilder';

class ContentlyNav extends Component {
  iaMapManager: typeof IaMapManager;
  _prevAccountId: string | null;

  static onSearch(query: string) {
    const encodedQuery = window.encodeURIComponent(query);
    if (window.__PORTFOLIO_USERNAME__) {
      const mainDomain = window.location.hostname.replace(/^[^.]+\./, '');
      window.location.href = `//${mainDomain}/search?query=${encodedQuery}`;
    } else {
      window.location.href = `${window.location.origin}/search?query=${encodedQuery}`;
    }
  }

  constructor(props: any) {
    super(props);
    this.iaMapManager = new IaMapManager();
    this._prevAccountId = null;
    this.state = {
      linkData: loadingIaMap,
      ready: false,
      accountData: [],
      activeAccount: { name: 'Loading', children: [], id: 0 },
      activePublication: { name: 'Loading', id: 0 },
    };
  }

  componentDidMount() {
    get(PlatformLinkBuilder.build('/api/v1/user'))
      .then((userData) => this.handleUserData(userData))
      .catch((err: any) => this.handleNavFetchError(err, '/api/v1/user'));
  }

  // Shared recovery for any nav bootstrap fetch (`/api/v1/user`,
  // `/api/v1/user/permissions`). Without a terminal handler the rejection
  // escapes to window.onunhandledrejection and Honeybadger pages on-call
  // (eng-maintenance#19549, then #20988 for the permissions path). A 401/403
  // means the Rails session expired while the SPA was still loaded — drive the
  // controlled session-expired recovery rather than leaving the nav in a
  // partially bootstrapped state with no path back to a valid auth state.
  // Non-auth failures (network, 5xx) are reported but NOT redirected — a
  // forced sign-out on a transient blip is worse UX than leaving the nav in
  // its Loading state until the next action retries.
  handleNavFetchError(err: any, apiPath: string) {
    const status = err && err.response && err.response.status;
    const isAuthFailure = !!(err && err.apiError) && (status === 401 || status === 403);

    if (isAuthFailure) {
      try { sessionStorage.removeItem('token'); } catch (_) { /* may be blocked */ }

      try {
        // The npm RUM SDK publishes itself as `window.DD_RUM` (defineGlobal),
        // NOT `window.datadogRum` — the latter was never assigned, so this
        // action silently no-op'd in prod until now.
        const w: any = window;
        if (w.DD_RUM && typeof w.DD_RUM.addAction === 'function') {
          w.DD_RUM.addAction('auth.session_expired_redirect', {
            from: window.location.pathname + window.location.search,
            apiPath,
            status,
          });
        }
      } catch (_) { /* telemetry must not block recovery */ }

      // Rails reads `params[:redirect_to]` in signed_in_root_path. Defensive
      // shape check: pathname is same-origin/leading-slash on every modern
      // browser, but guard belt-and-suspenders against double-slash and
      // backslash protocol-relative URLs to avoid contributing to the
      // pre-existing un-validated `redirect_to` consumer.
      const path = window.location.pathname + window.location.search;
      const safePath = /^\/[^/\\]/.test(window.location.pathname) ? path : '/';
      window.location.href = `/signin?reason=session_expired&redirect_to=${encodeURIComponent(safePath)}`;
      return;
    }

    logError(err instanceof Error ? err : new Error(`ContentlyNav: ${apiPath} fetch failed: ${err}`));
  }

  handleUserData(userData: any, isRetry = false) {
    const accountSwitcherLoader = userData.portfolioOnly
      ? new NullAccountSwitcherLoader()
      : new AccountSwitcherLoader(userData);
    const accountData = accountSwitcherLoader.serialize();

    const loadedActiveAccount = accountSwitcherLoader.activeAccount;
    const loadedActivePublication = accountSwitcherLoader.activePublication;

    if (!loadedActiveAccount || !loadedActivePublication) {
      // A non-portfolio user with no resolvable active account/publication means
      // /api/v1/user came back with empty accounts/brandProfiles. The dominant
      // cause is a transient mid-deploy response (pods restarting): the same
      // user resolves fine seconds later (confirmed on staging — 8/8 repeat
      // fetches returned full data). Re-fetch ONCE before degrading so a single
      // unlucky page load during a deploy window self-heals instead of dropping
      // the user into an account-switcher-less nav until they manually reload.
      //
      // The retry deliberately fires even when BOTH arrays are empty — that is
      // the actual shape a restarting pod returns, and it's the case we most
      // need to heal. We cannot distinguish "transiently empty for a staffed
      // user" from "genuinely unstaffed non-portfolio user" off a single empty
      // payload, so we pay one extra fetch for the latter before degrading. The
      // `isRetry` flag bounds this to exactly one retry — no loop is possible —
      // and `!userData.portfolioOnly` keeps portfolio-only users on the fast
      // path (they never have an account switcher to resolve).
      if (!isRetry && !userData.portfolioOnly) {
        get(PlatformLinkBuilder.build('/api/v1/user'))
          .then((retried) => this.handleUserData(retried, true))
          .catch(() => this.degradeToPortfolioNav(userData));
        return;
      }

      this.degradeToPortfolioNav(userData);
      return;
    }

    this.setState(
      {
        accountData,
        activeAccount: loadedActiveAccount.serialize(),
        activePublication: loadedActivePublication.serialize(),
        allPublications: accountSwitcherLoader.publications, // For v1 nav without "active account"
        username: userData.username,
        avatar: userData.avatar,
        cashoutBalance: userData.formattedBalance,
        portfolioOnly: userData.portfolioOnly,
      },
      this.setIa
    );
  }

  degradeToPortfolioNav(userData: any) {
    // The user has no resolvable staffed account/publication (every staffed
    // brand profile is inactive, or the API persistently returned empty even
    // after the retry above). Fall through to a portfolio-only-shaped state so
    // setIa still renders the nav — the avatar dropdown and its "Sign out" link
    // (permission: null) remain reachable, and the user isn't stuck with no way
    // to log out.
    //
    // This degrade is a GRACEFUL, self-healing, non-actionable event — NOT an
    // error worth paging on. The dominant population is admin/support/SSO
    // accounts (e.g. deep-linking into a story or /tasks from Kayako / the admin
    // tool) that legitimately have no staffed publication of their own, for whom
    // this fallback is the correct behavior. Diagnosed as expected noise:
    // `username` is an OPTIONAL column (null for ~1% of users incl. these admin
    // accounts), and a null username is orthogonal to account resolution — so it
    // never causes the degrade. (HB fault 131881644.)
    //
    // Therefore emit to Datadog RUM (queryable observability) instead of
    // `logError` → Honeybadger.notify, which would create an HB fault → prod
    // alert → engineering GHI for an event that needs no action. RUM keeps full
    // visibility over BOTH populations (the expected admin case AND a genuinely
    // staffed user who shouldn't be degrading — distinguishable by the
    // portfolioOnly/userId/path context below) without the alert path. Mirrors the
    // DD_RUM.addAction telemetry already used in handleNavFetchError above; do NOT
    // revert to logError or add a beforeNotify regex (the message can't
    // anchor-match only the expected population — see honeybadger-suppression.md).
    try {
      const w: any = window;
      if (w.DD_RUM && typeof w.DD_RUM.addAction === 'function') {
        // Same id useDatadog passes to setUser, so the action stays joinable on @usr.id.
        w.DD_RUM.addAction('nav.degraded_to_portfolio', {
          userId: w.__CURRENT_USER__?.id ?? 'unknown',
          username: userData?.username ?? 'none',
          portfolioOnly: !!userData?.portfolioOnly,
          path: `${window.location.pathname}${window.location.search}`,
        });
      }
    } catch (_) { /* telemetry must not block recovery */ }
    this.setState(
      {
        accountData: [],
        activeAccount: { id: null, name: '', children: [] },
        activePublication: { id: null, name: '' },
        allPublications: [],
        username: userData.username,
        avatar: userData.avatar,
        cashoutBalance: userData.formattedBalance,
        portfolioOnly: true,
      },
      this.setIa
    );
  }

  onIaVersionChange = (version: string) => {
    this.iaMapManager.currentIA = version;
    // this.setIa();
    // Temp refresh to add/remove rails switcher :(
    this.reload();
  };

  onActiveAccountChange = (activeAccount: Account) => {
    CookieJar.set('activeAccountId', activeAccount.id);
    if (AccountSwitcherLoader.handleAccountScopedPage(activeAccount.id)) return;
    if (AccountSwitcherLoader.handlePublicationScopedPage(activeAccount.children[0].id)) return;

    this.setState(
      {
        activeAccount,
        activePublication: activeAccount.children[0],
        ready: false,
      },
      this.setIa
    );
  };

  onActivePublicationChange = (activePublication: Publication) => {
    CookieJar.set('activePublicationId', activePublication.id);
    if (AccountSwitcherLoader.handlePublicationScopedPage(activePublication.id)) return;

    this.setState({ activePublication, ready: false }, this.setIa);
  };

  setIa() {
    const {
      activeAccount,
      activePublication,
      allPublications,
      username,
      permissionsData,
      cashoutBalance,
    } = this.state as any;

    // For nav v1 with no "active account", we need to check permissions on ALL pubs
    const pubs = this.iaMapManager.currentIA === 'V2' ? activeAccount.children : allPublications;

    const linkPermissionManager = new LinkPermissionManager(
      username,
      activeAccount.id,
      activePublication.id,
      pubs.map((bp: any) => bp.id)
    );

    const ia = this.iaMapManager.load(
      linkPermissionManager,
      this.onIaVersionChange,
      cashoutBalance
    );
    const accountPermissionsPromise = this.accountPermissionsPromise(
      this._prevAccountId,
      activeAccount.id,
      permissionsData
    );

    accountPermissionsPromise
      .then(({ data }) =>
        IaPermissionMapper(ia, data).then((permittedLinks) => {
          this._prevAccountId = activeAccount.id;
          this.setState({
            permissionsData: data,
            linkData: permittedLinks,
            ready: true,
          });
        })
      )
      // Terminal handler — `accountPermissionsPromise` fetches
      // `/api/v1/user/permissions`, which returns 401/403 when the Rails
      // session expires mid-session. Without this catch the rejection escaped
      // to window.onunhandledrejection and paged on-call (eng-maintenance
      // #20988). Route it through the same session-expired recovery as
      // `/api/v1/user` so a permissions-path auth failure behaves identically
      // to a user-path one (returning `false` from `accountPermissionsPromise`
      // for the portfolio-only case never rejects, so this only fires on a
      // real fetch failure).
      .catch((err: any) => this.handleNavFetchError(err, '/api/v1/user/permissions'));
  }

  accountPermissionsPromise = (
    previousAccountId: string | null,
    activeAccountId: string,
    permissionsData: any
  ) => {
    const { portfolioOnly } = this.state as any;

    if (portfolioOnly) {
      return Promise.resolve({
        data: {
          'user.portfolio': { enabled: true, ttl: 600 },
        },
      });
    }

    if (previousAccountId !== activeAccountId) {
      return get(PlatformLinkBuilder.build(`/api/v1/user/permissions`), false);
    }

    return Promise.resolve({ data: permissionsData });
  };

  // TMP for version switching
   
  reload() {
    window.location.reload();
  }

  showAccountSwitcher() {
    return this.iaMapManager.currentIA === 'V2';
  }

  render() {
    const {
      avatar,
      linkData,
      ready,
      accountData,
      activeAccount,
      activePublication,
      portfolioOnly,
    } = this.state as any;

    const accountSwitcher = this.showAccountSwitcher() ? (
      <AccountSelector
        ready={ready}
        accountLabel="Organization"
        accountOptions={accountData}
        onAccountChange={this.onActiveAccountChange}
        selectedAccount={activeAccount}
        subAccountLabel="Publication"
        onSubAccountChange={this.onActivePublicationChange}
        selectedSubAccount={activePublication}
      />
    ) : null;

    return (
      <Fragment>
        {accountSwitcher}
        <Nav
          data={linkData}
          ready={ready}
          onSearch={portfolioOnly ? 'undefined' : ContentlyNav.onSearch}
          searchPlaceholder="Find your stories"
          avatarUrl={avatar}
          theme={this.iaMapManager.currentTheme}
        />
      </Fragment>
    );
  }
}

export default ContentlyNav;
